import { FC, useEffect, useState } from 'react';
import {
  Button as AntButton,
  Modal,
  Select,
  Table,
  TableColumnsType,
} from 'antd';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { RootState } from '../../../../store/rootReducer';
import Button from '../../../Button';
import useAxios from '../../../../hooks/useAxios';
import config from '../../../../lib/config';
import Pagination from '../../../Pagination';
import Loader from '../../../Loader';
import ErrorResponse from '../../../ErrorResponse';
import { getCommonCodeLabel } from '../../../../lib/store';
import {
  IGetSpeakingList,
  ISpeakingList,
} from '../../../../models/newSpeaking.model';
import { getSpeakingList } from '../../../../api/newSpeaking.api';
import { antdTableStrSorter, getReverseYears } from '../../../../lib/util';

interface IProps {
  visible: boolean;
  setVisible: (visible: boolean) => void;
  setSkillExam: (speakingExamCode: number, speakingExamName: string) => void;
  eduStepCode: string;
  title?: string;
};

const SelectSkillExamModal: FC<IProps> = ({
  visible,
  setVisible,
  setSkillExam,
  eduStepCode,
  title,
}) => {
  const { t } = useTranslation();
  const { commonCodes } = useSelector((state: RootState) => state.commonCode);
  // TE0001 = 스피킹 시험 유형
  const TE0003 = commonCodes.find((item) => item.commonCode === 'TE0003')?.detailCode;
  // SY0005 = 연도
  const SY0005 = commonCodes.find((item) => item.commonCode === 'SY0005')?.detailCode;
  const { data: res, isLoading, error, execute } = useAxios<IGetSpeakingList>();

  const [speakingExamType, setSpeakingExamType] = useState<string>();
  const [year, setYear] = useState<string>();
  const [examName, setExamName] = useState('');
  const [params, setParams] = useState<any>({});
  const [pageNo, setPageNo] = useState(1);

  useEffect(() => {
    doGetSpeakingList({ ...params, pageNo });
  }, [pageNo]);

  const onSearch = () => {
    const params: any = { pageNo: 1 };
    if (speakingExamType) params.speakingExamType = speakingExamType;
    if (year) params.year = year;
    if (examName) params.examName = examName;
    setParams(params);
    doGetSpeakingList(params);
  };

  const doGetSpeakingList = (params: any) => {
    execute(getSpeakingList({
      ...params,
      eduStepCode,
      perPage: config.miniPageSize,
    }));
  };

  const columns: TableColumnsType<ISpeakingList> = [
    {
      title: t('cbt.speakTest.column1'),
      dataIndex: 'examName',
      key: 'examName',
      align: 'center',
      sorter: (a, b) => antdTableStrSorter<ISpeakingList>(a, b, 'examName'),
    },
    {
      title: t('cbt.speakTest.column3'),
      dataIndex: 'speakingExamType',
      key: 'speakingExamType',
      align: 'center',
      sorter: (a, b) => a.speakingExamType?.localeCompare(b.speakingExamType),
      render: (value) => getCommonCodeLabel('TE0003', value, 'ko'),
    },
    {
      title: t('cbt.speakTest.column4'),
      key: 'year',
      dataIndex: 'year',
      align: 'center',
      sorter: (a, b) => a.year?.localeCompare(b.year),
    },
    {
      title: '',
      dataIndex: 'speakingExamCode',
      key: 'speakingExamCode',
      align: 'center',
      render: (value, record) => (
        <div className='comm-btn__area justify-content-center'>
          <Button
            className="btn-basic small mint"
            onClick={() => {
              setSkillExam(value, record.examName);
              setVisible(false);
            }}
            text={t('common.button.select')}
          />
        </div>
      ),
    },
  ];

  return (
    <>
      {isLoading && <Loader />}
      <ErrorResponse error={error} />

      <Modal
        title={title ? title : t('cbt.test.title3')}
        visible={visible}
        onCancel={() => setVisible(false)}
        footer={[
          <div className='comm-btn__area justify-content-center'>	
            <AntButton key="cancel" className='btn-basic gray' onClick={() => setVisible(false)}>
              {t('common.button.cancel')}
            </AntButton>
          </div>
        ]}
      >
        <div className="flex comm-gap">
          <div className="flex-fill">
            <Select
              className="w-100"
              placeholder={t('cbt.speakTest.column3')}
              value={speakingExamType}
              onChange={(value) => setSpeakingExamType(value)}
            >
              <Select.Option value="">{t('common.text.all')}</Select.Option>
              {TE0003?.map((item) => (
                <Select.Option key={item.detailCode} value={item.detailCode}>
                  {item.korName}
                </Select.Option>
              ))}
            </Select>
          </div>
          <div className="flex-fill">
            <Select
              className="w-100"
              placeholder={t('cbt.speakTest.column4')}
              value={year}
              onChange={(value) => setYear(value)}
            >
              <Select.Option value="">{t('common.text.all')}</Select.Option>
              {getReverseYears(SY0005).map((item) => (
                <Select.Option key={item.detailCode} value={item.detailCode}>
                  {item.korName}
                </Select.Option>
              ))}
            </Select>
          </div>
          <input
            type="text"
            className="form-control w-200"
            placeholder={t('cbt.test.placeholder2')}
            value={examName}
            onChange={e => setExamName(e.target.value)}
            onKeyPress={e => {
              if (e.key === 'Enter') {
                onSearch();
              }
            }}
          />
          <div className='comm-btn__area'>
            <Button
              className="btn-basic small black"
              onClick={onSearch}
              text={t('common.button.search')}
            />
          </div>
        </div>
        <Table
          className='comm-table__area'
          columns={columns}
          dataSource={res?.data}
          pagination={{
            current: pageNo,
            pageSize: config.miniPageSize,
          }}
          rowKey="speakingExamCode"
        />
        <div className='comm-bx__between'>
          <div className="common-paging center">
            <Pagination
              count={res?.total || 0}
              page={pageNo}
              setPage={setPageNo}
              mini
            />
          </div>
        </div>
      </Modal>
    </>
  );
};

export default SelectSkillExamModal;
